/**
 *  Isotransformation
 *  from `enriched Text` to HTML element
 *  and from HTML element to `enriched Text`
 */
import React from 'react';
// TODO had some build issues
import ReactDomServer from 'react-dom/server';
import config from 'config/config';
import i18n from 'src/utils/translate';
import { MENTIONED_USERS_ID, UNDESIRABLE_CHARACTERS } from 'src/constants/regex';
import { isCookieConsentGiven, CookieCategory } from './cookie';

const isServer = typeof window === 'undefined';

const smileysMap = new Map([
  [':)', 'smile.gif'],
  [':-)', 'smile.gif'],
  [';)', 'wink.gif'],
  [';-)', 'wink.gif'],
  [':d', 'bigsmile.gif'],
  [':-d', 'bigsmile.gif'],
  [':(', 'sad.gif'],
  [':-(', 'sad.gif'],
  [':x', 'angry.gif'],
  [':|', 'confuse.gif'],
  [':-/', 'confuse.gif'],
  [':p', 'funny.gif'],
  [':-p', 'funny.gif'],
  [':o', 'surprise.gif'],
  [':-o', 'surprise.gif'],
  ['8-)', 'cool.gif'],
  [':\'(', 'cry.png'],
]);

const stringToKey = str => str.trim().substring(0, 16).replace(/\s/gi, '-').toLowerCase();

/**
 * Return an inline links to the user's profile
 * @param { userId, username } user's Data
 * @param editable boolean
 *
 */
export const getMention = function ({ userId, username }, editable = false, disableLink = false, isLoggedIn = true) {
  if (disableLink || !isLoggedIn) {
    return username;
  }

  return (
    <span data-link-container={true} key={`mention-${userId}`}>
      {editable
        ? <a contentEditable="false" data-id={userId}
            rel="nofollow">{username}
          </a>
        : <a data-id={userId} href={'/user/' + username}
            rel="nofollow">{username}
        </a>
      }
    </span>
  );
};

/**
 * Return an inline links to the an URL
 * @param { url } URL
 * @param editable boolean
 *
 */
export const getLink = function (
  { url, originalText, text, isWhiteList, youtubeID },
  editable = false,
  disableLink = false) {

  if (editable) {
    return originalText;
  }
  if (disableLink) {
    return text;
  }

  // We display a Youtube player only if the user has accepted functionality cookies
  if (youtubeID && !isServer && isCookieConsentGiven(CookieCategory.Functionality)) {
    const formattedYoutubeID = formatYoutubeID(youtubeID);
    return (
      <React.Fragment key={youtubeID}>
        <span className="youtubeVideoContainer">
          <iframe
            frameBorder="0"
            height="270"
            src={`//www.youtube.com/embed/${formattedYoutubeID}`}
            width="480"
          />
        </span>
        <button className="youtube-beacon-trigger" onClick={() => {
          window.HelpscoutBeacon.triggerHelpScoutArticle('109-why-my-youtube-video-is-not-playing');
        }}>
          {i18n.t('Not working as expected?')}
        </button>
        <br/>
      </React.Fragment>
    );
  }

  const anchorProps = { href: url, rel: 'nofollow ugc', target: '_blank' };
  if (isWhiteList) {
    delete anchorProps.rel;
  }

  return (
    <React.Fragment key={stringToKey(text)}>
      <a {...anchorProps}>{text}</a>
      { // When the user has rejected functionality cookies
        youtubeID &&
        <button className="youtube-beacon-trigger youtube-beacon-trigger--under-link" onClick={() => {
          window.HelpscoutBeacon.triggerHelpScoutArticle('109-why-my-youtube-video-is-not-playing');
        }}>
          ({i18n.t('Not working as expected?')})
        </button>
      }
    </React.Fragment>
  );
};

/**
 * Format the youtube ID to a valid '/embed/' format e.g. bMYCn-wR80?start=144
 * @param youtubeID
 */
export const formatYoutubeID = youtubeID => {
  if (youtubeID.indexOf('?') === -1) return youtubeID;
  const params = youtubeID.substr(youtubeID.indexOf('?') + 1);
  return youtubeID.replace(params, params.replace('s', '').replace('t=', 'start='));
};

/**
 * Return styled text
 * @param { text, style } text and style to display
 * @param editable boolean
 *
 */
export const getText = function ({ text, style }, editable = false) {
  let render = text;
  if (style.u) {
    render = <u>{render}</u>;
  }
  if (style.i) {
    render = editable ? <i>{render}</i> : <em>{render}</em>;
  }
  if (style.b) {
    render = editable ? <b>{render}</b> : <strong>{render}</strong>;
  }
  return render;
};

/**
 * Returns smiley image
 * @param { originalText } originalText containing smiley
 * @param editable boolean
 *
 */
export const getSmiley = function ({ originalText }, editable = false, index) {
  const smileyChars = originalText.toLowerCase();

  if (editable || !smileysMap.has(smileyChars)) {
    return originalText;
  }

  const fileName = smileysMap.get(smileyChars);
  return <img
    alt={originalText}
    key={`smiley-${originalText}-${index}`}
    src={config.cdn.smiley + fileName}
    title={originalText}
  />;
};

/**
 * add key to an nested arrays of elements
 * @param {item} an array or an react element
 * @param {index} index of the iterable
 *
 */
export const addKeys = function (item, index) {
  if (item.map) {
    return item.map(addKeys);
  }

  return item._isReactElement ? React.cloneElement(item, { key: index }) : item;
};

/**
 * Return an element to display
 * @param {enrichedText} enriched text
 *
 */
export const getElement = function (enrichedText = [], editable = false, disableLink = false, isLoggedIn = true) {
  if (!enrichedText && !enrichedText.length) {
    return null;
  }
  return enrichedText
    .reduce(([lastParagraph, ...paragraphs], item, index) => {
      switch (item.type) {
        case 'mention':
          return [[...lastParagraph, getMention(item, editable, disableLink, isLoggedIn)], ...paragraphs];
        case 'smiley':
          return [[...lastParagraph, getSmiley(item, editable, index)], ...paragraphs];
        case 'link':
          const link = getLink(item, editable, disableLink);
          return link.type === 'div' ?
            [[], link, lastParagraph, ...paragraphs] :
            [[...lastParagraph, link], ...paragraphs];
        case 'return':
          return [[], lastParagraph, ...paragraphs];
        case 'text':
          return [[...lastParagraph, getText(item, editable)], ...paragraphs];
        default:
          return [lastParagraph, ...paragraphs];
      }
    }, [[]]).reverse()
    .map(addKeys)
    .map((paragraph, index) => paragraph.type === 'div' ?
      React.cloneElement(paragraph, { key: 'p-' + index + Math.random() }) :
      <p key={'p-' + index + Math.random()}>{paragraph}</p>);
};

/**
 * Return an description for SEO purpose
 * @param {enrichedText} enriched text
 *
 */
export const getSeoDescription = function (enrichedText) {
  return enrichedText
    .reduce((description, item) => {
      switch (item.type) {
        case 'mention':
          return description + item.username;
        case 'text':
        case 'link':
          return description + item.text;
        case 'return':
          return description + ' ';
        case 'smiley':
          return description + item.originalText;
        default:
          return description + (item.text || '');
      }
    }, '');
};

/**
 * Return an HTML for content editor
 * @param {enrichedText} enriched text
 *
 */
export const getEditableHTML = function (enrichedText) {
  return getElement(enrichedText, true).reduce(
    (editableHTML, paragraph) => editableHTML + ReactDomServer.renderToStaticMarkup(paragraph),
    '',
  );
};

const getCleanNodeValue = (node) => {
  let safeNodeValue = node.nodeValue || '';
  safeNodeValue = safeNodeValue.replace(MENTIONED_USERS_ID, '@ [$1]');
  UNDESIRABLE_CHARACTERS.forEach(el =>
    safeNodeValue = safeNodeValue.replace(el.pattern, el.replacement),
  );
  return safeNodeValue;
};

// rough polyfill of innerText to have a homogeneous behaviour
export const parseNode = function (node) {
  let str = '';
  const mentionedUserIds = {};
  const childNodes = Array.from(node.childNodes);

  const recursive = (subNode, before, after) => {
    const { str: innerStr, mentionedUserIds: innerMentionedUserIds} = parseNode(subNode);
    Object.assign(mentionedUserIds, innerMentionedUserIds);
    str += `${before}${innerStr}${after}`;
  };

  childNodes.forEach((child) => {
    switch (child.nodeName) {
      // escape mentions or links
      case 'A':
        const id = child.getAttribute('data-id');
        if (!id) {
          recursive(child, '', '');
          break;
        }
        str += `@[${id}]`;
        mentionedUserIds[id] = true;
        break;
      // replace unwanted mentions format
      // we use the same formatting than FB
      case '#text':
        str += getCleanNodeValue(child);
        break;
      case 'BR':
        if (node.lastChild === child && (node.nodeName === 'DIV' || node.nodeName === 'P')) {
          break;
        }
        str += '\n';
        break;
      case 'DIV':
      case 'P':
        recursive(child, '', '\n');
        break;
      case 'U':
        recursive(child, '[u]', '[/u]');
        break;
      case 'I':
      case 'EM':
        recursive(child, '[i]', '[/i]');
        break;
      case 'B':
      case 'STRONG':
        recursive(child, '[b]', '[/b]');
        break;
      default:
        recursive(child, '', '');
        break;
    }
  });
  return { str, mentionedUserIds };
};
